import { ColDefinitionDetails } from '../../components';
import { GetColab } from '../../modules/Colab/types';
import { useUserContext } from '../../modules/Users/UserContext';
import { DetailsSection, DetailsSectionHeading, formatDate } from '../shared/utils';
import ColabStatusCell from './colabStatusCell';
import ColabCategory from './colabCategory';

type ColabDetailsOverviewProps = { colabEntity?: GetColab };

const labels = {
  transcribed: 'Transcribed Feedback',
  translated: 'Translated Text (EN)',
  created: 'Created',
  timestamp: 'Last updated',
  vehicleModel: 'Car Model',
  vehicleModelYear: 'Car Model Year',
  systemLanguage: 'System Language',
  vehicleMarket: 'Market',
  vin: 'VIN',
  VIN: 'VIN',
  age: 'Age',
  email: 'Customer Email Address',
  category: 'Category',
  fromAppName: 'Coming from which App',
  fromAppVersion: 'App Version',
  ihuSoftwareVersion: 'IHU SW Version',
  dhuVersion: 'DHU SW Version',
  partitionKey: 'PartitionKey',
  rowKey: 'RowKey',
  errors: 'Errors',
  reference: 'Reference',
};

const additionalLabels = {
  status: 'Status',
};

function ColabDetailsOverview({ colabEntity }: ColabDetailsOverviewProps) {
  function hasAccess(scope: string) {
    const { checkAccess } = useUserContext();
    return checkAccess(scope);
  }

  function labelValueHandler(key: string, value: string | number | undefined) {
    if (value === undefined) return '';
    if (key === 'created') return formatDate(value.toString());
    if (key === 'timestamp') return formatDate(value.toString());
    if (key === 'vehicleModel' && value === 'CX11') return 'CX11 A1';
    else return value;
  }

  function shouldShowDetailValue(key: string, value: string | number) {
    if (key === 'category') return true;
    return value !== '';
  }

  function inCarDeviceTypeHandler(orderedRenamedRecord: { [key: string]: string | number }) {
    if (orderedRenamedRecord['Car Model'] === 'CX11 A1') delete orderedRenamedRecord['DHU SW Version'];
    else delete orderedRenamedRecord['IHU SW Version'];
  }

  if (colabEntity) {
    const orderedRenamedRecord: { [key: string]: string | number } = {};

    Object.entries(labels).forEach(([key, value]) => {
      if (key in colabEntity) {
        const detailValue = labelValueHandler(key, colabEntity[key as keyof GetColab]);
        if (shouldShowDetailValue(key, detailValue)) orderedRenamedRecord[value] = detailValue;
      }
    });
    Object.entries(additionalLabels).forEach(([, value]) => {
      orderedRenamedRecord[value] = '';
    });

    inCarDeviceTypeHandler(orderedRenamedRecord);

    return (
      <DetailsSection>
        <DetailsSectionHeading>Details</DetailsSectionHeading>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
          {Object.entries(orderedRenamedRecord).map(([labelName, value]) => {
            if (labelName === 'Status') {
              return (
                <ColDefinitionDetails key={labelName} label={labelName}>
                  <ColabStatusCell colabEntity={colabEntity} />
                </ColDefinitionDetails>
              );
            }
            if (labelName === 'Category') {
              return (
                <ColDefinitionDetails key={labelName} label={labelName}>
                  <ColabCategory colabEntity={colabEntity} />
                </ColDefinitionDetails>
              );
            }
            if ((labelName === 'Age' || labelName === 'Customer Email Address') && hasAccess('COLAB_FULL_ACCESS')) {
              return (
                <ColDefinitionDetails key={labelName} label={labelName}>
                  {value}
                </ColDefinitionDetails>
              );
            } else if (labelName !== 'Age' && labelName !== 'Customer Email Address') {
              return (
                <ColDefinitionDetails key={labelName} label={labelName}>
                  {value}
                </ColDefinitionDetails>
              );
            }
          })}
        </dl>
      </DetailsSection>
    );
  } else {
    return <div className="text-center text-2xl m-8 text-gray-500">No Record Found</div>;
  }
}

export default ColabDetailsOverview;
